import type { ReactNode } from 'react';
import {
  Button,
  FilterButton,
  Popover,
  Select,
  TabPills,
} from '@skupreme/shared/ui';
import type { V2ProvidersFilter } from '../../hooks/use-providers';
import { getProviderIntegrationName } from '../../lib/integration';
import { providerBrandName } from '../../lib/provider-brand';
import type { StorageProviderV2ListResponse } from '../../types-v2';

const ALL = 'all';
const TRIGGER_WIDTH = 'sk:w-52';

type Counts = StorageProviderV2ListResponse['counts'];
type Facets = StorageProviderV2ListResponse['facets'];

const countryName = (code: string) => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

const providerName = (type: string) =>
  providerBrandName(type) ?? getProviderIntegrationName(type) ?? type;

/** EN-4134: All, Active and Inactive, each with its count, as the prototype's list. */
export function ProvidersStatusTabs({
  counts,
  filter,
  onChange,
}: {
  counts: Counts;
  filter: V2ProvidersFilter;
  onChange: (next: V2ProvidersFilter) => void;
}) {
  return (
    <TabPills
      label="Provider status"
      selectedKey={filter.status ?? ALL}
      onSelectionChange={key =>
        onChange({
          ...filter,
          status: key === 'active' || key === 'inactive' ? key : undefined,
        })
      }
      items={[
        { id: ALL, label: 'All', count: counts?.all },
        { id: 'active', label: 'Active', count: counts?.active },
        { id: 'inactive', label: 'Inactive', count: counts?.inactive },
      ]}
    />
  );
}

/* One control in the popover, named on the left like the catalog filters. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="sk:flex sk:items-center sk:justify-between sk:gap-3 sk:px-3 sk:py-2">
      <span className="sk:text-dense sk:font-medium sk:text-fg">{label}</span>
      {children}
    </div>
  );
}

/** EN-4134: the provider type and country filters, behind one Filters button, offering what the rows have. */
export function ProvidersV2Filters({
  facets,
  filter,
  onChange,
}: {
  facets: Facets;
  filter: V2ProvidersFilter;
  onChange: (next: V2ProvidersFilter) => void;
}) {
  const active = (filter.provider ? 1 : 0) + (filter.country ? 1 : 0);

  return (
    <Popover
      aria-label="Filters"
      placement="bottom end"
      trigger={<FilterButton count={active} />}
    >
      <div className="sk:flex sk:w-80 sk:flex-col">
        <div className="sk:flex sk:items-center sk:justify-between sk:gap-6 sk:border-b sk:border-border-subtle sk:px-3 sk:py-2">
          <span className="sk:text-xs sk:font-semibold sk:text-fg-tertiary">
            Filters
          </span>
          {active > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="sk:text-dense"
              onPress={() =>
                onChange({ ...filter, provider: undefined, country: undefined })
              }
            >
              Clear all
            </Button>
          ) : null}
        </div>
        <Row label="Provider">
          <Select
            aria-label="Provider"
            value={filter.provider ?? ALL}
            options={[
              { value: ALL, label: 'All providers' },
              ...(facets?.providers ?? []).map(type => ({
                value: type,
                label: providerName(type),
              })),
            ]}
            onChange={value =>
              onChange({
                ...filter,
                provider: value === ALL ? undefined : value,
              })
            }
            className={TRIGGER_WIDTH}
          />
        </Row>
        <Row label="Country">
          <Select
            aria-label="Country"
            value={filter.country ?? ALL}
            options={[
              { value: ALL, label: 'All countries' },
              ...(facets?.countries ?? []).map(code => ({
                value: code,
                label: countryName(code),
              })),
            ]}
            onChange={value =>
              onChange({
                ...filter,
                country: value === ALL ? undefined : value,
              })
            }
            className={TRIGGER_WIDTH}
          />
        </Row>
      </div>
    </Popover>
  );
}
